import axios from 'axios';
import createSignedRequest from './createSignedRequest';
import createUnsignedRequest from './createUnsignedRequest';
import { toggleAlertMessage } from '../action';

const { apiURL } = require('./apiURL');

const MY_BASE = `${apiURL}/my/survey`;
const PUBLIC_BASE = `${apiURL}/public/survey`;

export const Types = {
  SURVEY_SET_LOADING: 'SURVEY_SET_LOADING',
  SURVEY_SET_ERROR: 'SURVEY_SET_ERROR',
  SURVEY_SET_REPORTING_PERIODS: 'SURVEY_SET_REPORTING_PERIODS',
  SURVEY_SET_CAMPAIGNS: 'SURVEY_SET_CAMPAIGNS',
  SURVEY_SET_CURRENT_CAMPAIGN: 'SURVEY_SET_CURRENT_CAMPAIGN',
  SURVEY_SET_CAMPAIGN_RESULTS: 'SURVEY_SET_CAMPAIGN_RESULTS',
  SURVEY_SET_CAMPAIGN_RESPONSES: 'SURVEY_SET_CAMPAIGN_RESPONSES',
  SURVEY_SET_TOKEN_META: 'SURVEY_SET_TOKEN_META',
  SURVEY_SET_TOKEN_QUESTIONS: 'SURVEY_SET_TOKEN_QUESTIONS',
  SURVEY_SET_DRAFT_RESPONSE_DATA: 'SURVEY_SET_DRAFT_RESPONSE_DATA',
  SURVEY_SET_CAMPAIGN_REPORT: 'SURVEY_SET_CAMPAIGN_REPORT',
};

const setLoading = (section, value) => ({ type: Types.SURVEY_SET_LOADING, payload: { section, value } });
const setError = (payload) => ({ type: Types.SURVEY_SET_ERROR, payload });
const setReportingPeriods = (payload) => ({ type: Types.SURVEY_SET_REPORTING_PERIODS, payload });
const setCampaigns = (payload) => ({ type: Types.SURVEY_SET_CAMPAIGNS, payload });
const setCurrentCampaign = (payload) => ({ type: Types.SURVEY_SET_CURRENT_CAMPAIGN, payload });
const setCampaignResults = (payload) => ({ type: Types.SURVEY_SET_CAMPAIGN_RESULTS, payload });
const setCampaignResponses = (payload) => ({ type: Types.SURVEY_SET_CAMPAIGN_RESPONSES, payload });
const setTokenMeta = (payload) => ({ type: Types.SURVEY_SET_TOKEN_META, payload });
const setTokenQuestions = (payload) => ({ type: Types.SURVEY_SET_TOKEN_QUESTIONS, payload });
const setDraftResponseData = (payload) => ({ type: Types.SURVEY_SET_DRAFT_RESPONSE_DATA, payload });
const setCampaignReport = (payload) => ({ type: Types.SURVEY_SET_CAMPAIGN_REPORT, payload });

const orgHeader = (state) => ({ Organisation: String(state.currentOrganisation) });

const unwrapErrorMessage = async (error, fallback = 'Request failed') => {
  const data = error?.response?.data;
  if (error?.response?.status === 403 && data?.error === 'feature_not_entitled') {
    return {
      code: data.error,
      featureKey: data.featureKey,
      message: data.message || 'This feature is not included in your current plan.',
    };
  }
  if (error?.response?.data?.error) return error.response.data.error;
  if (Array.isArray(error?.response?.data?.errors) && error.response.data.errors[0]?.message) {
    return error.response.data.errors[0].message;
  }
  if (error?.message) return error.message;
  if (error instanceof Response) {
    try {
      const data = await error.json();
      if (data?.error) return data.error;
      if (Array.isArray(data?.errors) && data.errors[0]?.message) return data.errors[0].message;
    } catch (e) {
      // Ignore JSON parse issue and keep fallback.
    }
  }
  return fallback;
};

const errorMessageText = (error) =>
  typeof error === 'object' && error?.message ? error.message : String(error || 'Request failed');

const buildJsonBody = (data) => JSON.stringify(data == null ? {} : data);

const unwrapCampaignResponse = (body) => {
  if (body == null || typeof body !== 'object') return null;
  if (body.campaign && typeof body.campaign === 'object' && !Array.isArray(body.campaign)) return body.campaign;
  if (body.data && typeof body.data === 'object' && (body.data.id != null || Array.isArray(body.data.invitations))) {
    return body.data;
  }
  return body;
};

const signedCall = async (method, endpoint, data, headers = {}) => {
  const request = await createSignedRequest(method, endpoint, data == null ? '' : buildJsonBody(data), headers);
  return axios(request);
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const parseFilenameFromContentDisposition = (header) => {
  if (!header || typeof header !== 'string') return null;
  const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, '').trim());
    } catch (e) {
      return null;
    }
  }
  const asciiMatch = /filename=["']?([^"'\n;]+)["']?/i.exec(header);
  return asciiMatch ? asciiMatch[1].trim() : null;
};

const unsignedCall = async (method, endpoint, data, headers = {}) => {
  const response = await createUnsignedRequest(method, endpoint, data == null ? null : buildJsonBody(data), headers);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(payload?.error || 'Request failed');
    err.response = { data: payload, status: response.status };
    throw err;
  }
  return payload;
};

/** One in-flight GET /public/survey/:token/responses per token (draft merge + gating share the same payload). */
const tokenSurveyResponsesPayloadByToken = new Map();

const fetchTokenSurveyResponsesPayloadCached = (token) => {
  if (!token) return Promise.resolve(null);
  let pending = tokenSurveyResponsesPayloadByToken.get(token);
  if (!pending) {
    pending = unsignedCall('GET', `${PUBLIC_BASE}/${token}/responses`)
      .then((data) => data ?? null)
      .catch(() => null);
    tokenSurveyResponsesPayloadByToken.set(token, pending);
  }
  return pending;
};

const mapReportingPeriods = (payload) => {
  const reportingPeriods = Array.isArray(payload?.reportingPeriods) ? payload.reportingPeriods : [];

  return reportingPeriods
    .map((period) => {
      if (!period?.reportingPeriodStart || !period?.reportingPeriodEnd) return null;
      return {
        value: `${period.reportingPeriodStart}/${period.reportingPeriodEnd}`,
        label: period.label || String(period.reportingYear || ''),
        reportingYear: period.reportingYear,
        reportingPeriodStart: period.reportingPeriodStart,
        reportingPeriodEnd: period.reportingPeriodEnd,
        isCurrent: Boolean(period.isCurrent),
      };
    })
    .filter(Boolean);
};

export const getReportingPeriods = () => async (dispatch, getState) => {
  dispatch(setLoading('reportingPeriods', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('GET', `${MY_BASE}/reporting-periods`, null, orgHeader(state));
    const options = mapReportingPeriods(response?.data || {});
    dispatch(setReportingPeriods(options));
    return options;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to fetch reporting periods')));
    dispatch(setReportingPeriods([]));
    return [];
  } finally {
    dispatch(setLoading('reportingPeriods', false));
  }
};

export const listCampaigns = () => async (dispatch, getState) => {
  dispatch(setLoading('campaigns', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('GET', `${MY_BASE}/campaigns`, null, orgHeader(state));
    dispatch(
      setCampaigns({
        campaigns: response?.data?.campaigns || [],
        invitationTotals: response?.data?.invitationTotals ?? null,
      }),
    );
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to fetch campaigns')));
  } finally {
    dispatch(setLoading('campaigns', false));
  }
};

export const createCampaign = (payload) => async (dispatch, getState) => {
  dispatch(setLoading('createCampaign', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('POST', `${MY_BASE}/campaign`, payload, orgHeader(state));
    dispatch(toggleAlertMessage(true, 'Survey campaign created.', 'success', 3000));
    return response?.data;
  } catch (error) {
    const message = await unwrapErrorMessage(error, 'Failed to create campaign');
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return null;
  } finally {
    dispatch(setLoading('createCampaign', false));
  }
};

export const getCampaign = (campaignId) => async (dispatch, getState) => {
  dispatch(setLoading('campaign', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('GET', `${MY_BASE}/campaign/${campaignId}`, null, orgHeader(state));
    const campaign = unwrapCampaignResponse(response?.data) || null;
    dispatch(setCurrentCampaign(campaign));
    return campaign;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to fetch campaign')));
    return null;
  } finally {
    dispatch(setLoading('campaign', false));
  }
};

export const setCampaignFacilities = (campaignId, payload) => async (dispatch, getState) => {
  dispatch(setLoading('facilities', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('POST', `${MY_BASE}/campaign/${campaignId}/facilities`, payload, orgHeader(state));
    const partial = unwrapCampaignResponse(response?.data);
    const prev = getState().survey?.currentCampaign;
    if (prev && String(prev.id) === String(campaignId) && partial && typeof partial === 'object') {
      dispatch(
        setCurrentCampaign({
          ...prev,
          ...(partial.facilityIds !== undefined ? { facilityIds: partial.facilityIds } : {}),
          ...(partial.fteOverrides !== undefined ? { fteOverrides: partial.fteOverrides } : {}),
        }),
      );
    }
    dispatch(toggleAlertMessage(true, 'Facilities updated.', 'success', 3000));
    return response?.data || null;
  } catch (error) {
    const message = await unwrapErrorMessage(error, 'Failed to update facilities');
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return null;
  } finally {
    dispatch(setLoading('facilities', false));
  }
};

export const addInvitations = (campaignId, payload) => async (dispatch, getState) => {
  dispatch(setLoading('invitations', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('POST', `${MY_BASE}/campaign/${campaignId}/invitations`, payload, orgHeader(state));
    dispatch(toggleAlertMessage(true, 'Invitations saved.', 'success', 3000));
    await dispatch(getCampaign(campaignId));
    return response?.data || null;
  } catch (error) {
    const message = await unwrapErrorMessage(error, 'Failed to save invitations');
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return null;
  } finally {
    dispatch(setLoading('invitations', false));
  }
};

export const deleteInvitation = (campaignId, invitationId) => async (dispatch, getState) => {
  if (!campaignId || !invitationId) return false;
  dispatch(setLoading('deleteInvitation', true));
  dispatch(setError(null));
  try {
    const state = getState();
    await signedCall('DELETE', `${MY_BASE}/campaign/${campaignId}/invitations/${invitationId}`, null, orgHeader(state));
    dispatch(toggleAlertMessage(true, 'Invitation removed.', 'success', 3000));
    await dispatch(getCampaign(campaignId));
    return true;
  } catch (error) {
    const message = await unwrapErrorMessage(error, 'Failed to remove invitation');
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return false;
  } finally {
    dispatch(setLoading('deleteInvitation', false));
  }
};

export const remindInvitation = (campaignId, invitationId) => async (dispatch, getState) => {
  if (!campaignId || !invitationId) return false;
  dispatch(setError(null));
  try {
    const state = getState();
    await signedCall(
      'POST',
      `${MY_BASE}/campaign/${campaignId}/invitations/${invitationId}/remind`,
      {},
      orgHeader(state),
    );
    dispatch(toggleAlertMessage(true, 'Invitation reminder sent.', 'success', 3000));
    await dispatch(getCampaign(campaignId));
    return true;
  } catch (error) {
    const message = await unwrapErrorMessage(error, 'Failed to send reminder');
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return false;
  }
};

const campaignAction = (actionLabel, endpointPath) => (campaignId) => async (dispatch, getState) => {
  dispatch(setLoading(actionLabel, true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('POST', `${MY_BASE}/campaign/${campaignId}/${endpointPath}`, {}, orgHeader(state));
    dispatch(toggleAlertMessage(true, `${actionLabel} completed.`, 'success', 3000));
    await dispatch(getCampaign(campaignId));
    return response?.data || null;
  } catch (error) {
    const message = await unwrapErrorMessage(error, `Failed to ${actionLabel}`);
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return null;
  } finally {
    dispatch(setLoading(actionLabel, false));
  }
};

export const sendInvitations = campaignAction('send', 'send');
export const remindNonRespondents = campaignAction('remind', 'remind');
export const closeCampaign = campaignAction('close', 'close');
export const reopenCampaign = campaignAction('reopen', 'reopen');

export const getCampaignReport = (campaignId) => async (dispatch, getState) => {
  dispatch(setLoading('report', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('GET', `${MY_BASE}/campaign/${campaignId}/report`, null, orgHeader(state));
    dispatch(setCampaignReport(response?.data || null));
    return response?.data || null;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to fetch campaign report')));
    return null;
  } finally {
    dispatch(setLoading('report', false));
  }
};

export const getCampaignResults = (campaignId) => async (dispatch, getState) => {
  dispatch(setLoading('results', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('GET', `${MY_BASE}/campaign/${campaignId}/results`, null, orgHeader(state));
    dispatch(setCampaignResults(response?.data || null));
    return response?.data || null;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to fetch campaign results')));
    return null;
  } finally {
    dispatch(setLoading('results', false));
  }
};

export const getCampaignResponses = (campaignId) => async (dispatch, getState) => {
  dispatch(setLoading('responses', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall('GET', `${MY_BASE}/campaign/${campaignId}/responses`, null, orgHeader(state));
    dispatch(setCampaignResponses(response?.data?.responses || response?.data || []));
    return response?.data || [];
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to fetch campaign responses')));
    return [];
  } finally {
    dispatch(setLoading('responses', false));
  }
};

export const exportCampaignResponsesCsv = (campaignId, campaignName) => async (dispatch, getState) => {
  if (!campaignId) return false;
  dispatch(setLoading('exportCampaign', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const endpoint = `${MY_BASE}/campaign/${campaignId}/export/responses.csv`;
    const request = await createSignedRequest('GET', endpoint, null, orgHeader(state));
    const response = await axios({ ...request, responseType: 'blob' });

    const blob = response.data;
    if (blob instanceof Blob && blob.type && blob.type.includes('application/json')) {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      throw new Error(parsed?.error || parsed?.message || 'Export failed');
    }

    const fromHeader = parseFilenameFromContentDisposition(response.headers?.['content-disposition']);
    const safe = String(campaignName || 'survey-campaign')
      .replace(/[^\w.-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'survey-campaign';
    downloadBlob(blob, fromHeader || `${safe}-responses.csv`);
    dispatch(toggleAlertMessage(true, 'Responses export downloaded.', 'success', 3000));
    return true;
  } catch (error) {
    let message = await unwrapErrorMessage(error, 'Failed to export responses');
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        if (parsed?.error) message = parsed.error;
      } catch (e) {
        // keep message
      }
    }
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return false;
  } finally {
    dispatch(setLoading('exportCampaign', false));
  }
};

export const flagResponse = (campaignId, responseId, payload) => async (dispatch, getState) => {
  dispatch(setLoading('flagResponse', true));
  dispatch(setError(null));
  try {
    const state = getState();
    const response = await signedCall(
      'POST',
      `${MY_BASE}/campaign/${campaignId}/response/${responseId}/flag`,
      payload,
      orgHeader(state),
    );
    dispatch(toggleAlertMessage(true, 'Response updated.', 'success', 3000));
    await dispatch(getCampaignResponses(campaignId));
    return response?.data || null;
  } catch (error) {
    const message = await unwrapErrorMessage(error, 'Failed to flag response');
    dispatch(setError(message));
    dispatch(toggleAlertMessage(true, errorMessageText(message), 'error', 4000));
    return null;
  } finally {
    dispatch(setLoading('flagResponse', false));
  }
};

export const getTokenSurveyMeta = (token) => async (dispatch) => {
  dispatch(setLoading('tokenMeta', true));
  dispatch(setError(null));
  try {
    const data = await unsignedCall('GET', `${PUBLIC_BASE}/${token}`);
    dispatch(setTokenMeta(data || null));
    return data || null;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to load survey token metadata')));
    return null;
  } finally {
    dispatch(setLoading('tokenMeta', false));
  }
};

export const getTokenSurveyQuestions = (token) => async (dispatch) => {
  dispatch(setLoading('tokenQuestions', true));
  dispatch(setError(null));
  try {
    const data = await unsignedCall('GET', `${PUBLIC_BASE}/${token}/questions`);
    dispatch(setTokenQuestions(data?.questions || []));
    if (data?.responseData && typeof data.responseData === 'object') {
      dispatch(setDraftResponseData(data.responseData));
    }
    return data || null;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to load survey questions')));
    return null;
  } finally {
    dispatch(setLoading('tokenQuestions', false));
  }
};

/** Pull answer map from GET /public/survey/:token/responses (shape varies by backend). */
export const extractTokenSurveyResponseData = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const direct =
    payload.responseData ??
    payload.data?.responseData ??
    payload.draft?.responseData ??
    payload.response?.responseData;
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) return direct;
  if (Array.isArray(payload.responses)) {
    const row =
      payload.responses.find((r) => r?.status === 'draft' || r?.isDraft === true) || payload.responses[0];
    const fromRow = row?.responseData;
    if (fromRow && typeof fromRow === 'object' && !Array.isArray(fromRow)) return fromRow;
  }
  return null;
};

/** Raw JSON from GET /public/survey/:token/responses (deduped per token with {@link getTokenSurveyResponses}). */
export const getTokenSurveyResponsesPayload = (token) => async () => fetchTokenSurveyResponsesPayloadCached(token);

export const getTokenSurveyResponses = (token) => async () => {
  const data = await fetchTokenSurveyResponsesPayloadCached(token);
  return extractTokenSurveyResponseData(data);
};

export const saveTokenResponseDraft = (token, responseData) => async (dispatch) => {
  dispatch(setLoading('saveDraft', true));
  dispatch(setError(null));
  try {
    const data = await unsignedCall('POST', `${PUBLIC_BASE}/${token}/save`, { responseData });
    dispatch(setDraftResponseData(responseData || {}));
    return data || null;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to save draft')));
    return null;
  } finally {
    dispatch(setLoading('saveDraft', false));
  }
};

export const submitTokenResponse = (token, responseData) => async (dispatch) => {
  dispatch(setLoading('submit', true));
  dispatch(setError(null));
  try {
    const data = await unsignedCall('POST', `${PUBLIC_BASE}/${token}/submit`, { responseData });
    dispatch(setDraftResponseData(responseData || {}));
    return data || null;
  } catch (error) {
    dispatch(setError(await unwrapErrorMessage(error, 'Failed to submit response')));
    return null;
  } finally {
    dispatch(setLoading('submit', false));
  }
};

export const setLocalDraftResponseData = (responseData) => (dispatch) => {
  dispatch(setDraftResponseData(responseData || {}));
};

export const clearSurveyError = () => (dispatch) => dispatch(setError(null));

export const Actions = {
  Types,
  getReportingPeriods,
  listCampaigns,
  createCampaign,
  getCampaign,
  setCampaignFacilities,
  addInvitations,
  deleteInvitation,
  remindInvitation,
  sendInvitations,
  remindNonRespondents,
  closeCampaign,
  reopenCampaign,
  getCampaignReport,
  getCampaignResults,
  getCampaignResponses,
  exportCampaignResponsesCsv,
  flagResponse,
  getTokenSurveyMeta,
  getTokenSurveyQuestions,
  getTokenSurveyResponses,
  getTokenSurveyResponsesPayload,
  saveTokenResponseDraft,
  submitTokenResponse,
  setLocalDraftResponseData,
  clearSurveyError,
};
